import { useCallback, useEffect, useRef, useState } from 'react';
import type { DeployRequestStatus } from '../api';

/**
 * Re-exported from the API layer so that hook consumers and the API
 * contract always share the exact same type — a single source of truth.
 */
export type ApprovalStatus = DeployRequestStatus;

export interface UseApprovalStatusResult {
  /** Current approval status, null only while the initial fetch is in flight. */
  approvalStatus: ApprovalStatus | null;
  /** True until the first fetch completes (success or error). */
  loading: boolean;
  /** Re-fetch the approval status on demand. */
  refetch: () => void;
}

/**
 * Generic hook that fetches an approval status on mount.
 *
 * The caller provides a fetcher function that returns the current ApprovalStatus.
 * This keeps the hook decoupled from any specific API or platform feature.
 *
 * @param fetchStatus - async function that fetches the current approval status
 */
export function useApprovalStatus(
  fetchStatus: () => Promise<ApprovalStatus>,
): UseApprovalStatusResult {
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [fetchKey, setFetchKey] = useState(0);

  // Track the fetcher identity so we can reset state when it changes
  const prevFetchStatusRef = useRef(fetchStatus);

  const refetch = useCallback(() => setFetchKey(k => k + 1), []);

  useEffect(() => {
    // Reset state when the fetcher changes (e.g. environment switch)
    if (prevFetchStatusRef.current !== fetchStatus) {
      prevFetchStatusRef.current = fetchStatus;
      setApprovalStatus(null);
      setLoading(true);
    }

    let cancelled = false;
    fetchStatus()
      .then(result => {
        if (!cancelled) setApprovalStatus(result);
      })
      .catch(() => {
        if (!cancelled) setApprovalStatus(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchStatus, fetchKey]);

  return { approvalStatus, loading, refetch };
}
